import { useAuth } from "../../../auth/AuthContext";
import { FormEvent, useCallback, useEffect, useState, type CSSProperties } from "react";
import { PanelLoading } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { qualityApi, type QualityParameter } from "../../../api/quality";
import type { ApiError } from "../../../api/client";
import { SUPPORTED_LOCALES } from "../../../i18n";
import { useToast } from "../../../components/Toast";
import { useConfirm } from "../../../components/ConfirmDialog";
import { localizedName } from "../../../lib/localizedName";
import Ic from "../../../components/icons.jsx";

/**
 * The quality-parameter codebook.
 *
 * What the lab measures is a list, not a set of columns: adding a parameter is a
 * row somebody types in, and the delivery intake form rebuilds itself from the list.
 *
 * Names are edited per language, the same way markets and offer stages are — the
 * name a Serbian user sees is data, never a translation key.
 */
export default function AdminQualityParametersPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("quality_parameters:manage");
  const canFull = hasPermission("quality_parameters:delete");
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [parameters, setParameters] = useState<QualityParameter[] | null>(null);
  const [editing, setEditing] = useState<QualityParameter | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setParameters(await qualityApi.listParameters(false));
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function retire(p: QualityParameter) {
    const ok = await confirm({
      message: t(
        "admin.quality.confirmRetire",
        "Retire {{name}}? Past settlements keep it — it stops being offered to the lab.",
        { name: localizedName({ name: p.key, nameI18n: p.nameI18n }, i18n.resolvedLanguage ?? "sr") },
      ),
    });
    if (!ok) return;
    try {
      await qualityApi.deactivateParameter(p.id);
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>{error}</p></div>;

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("admin.quality.title", "Quality parameters")}</h1>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
            {t(
              "admin.quality.subtitle",
              "What the lab measures on an inbound delivery. The intake form is built from this list.",
            )}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" disabled={!canWrite} onClick={() => setCreating(true)}>
            <Ic.plus /> {t("admin.quality.new", "New parameter")}
          </button>
        </div>
      </div>

      {!parameters ? (
        <PanelLoading text={t("common.loading")} />
      ) : parameters.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.quality.empty", "No parameters defined.")}</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("admin.quality.col.key", "Key")}</th>
              <th>{t("admin.quality.col.name", "Name")}</th>
              <th>{t("admin.quality.col.unit", "Unit")}</th>
              <th>{t("admin.quality.col.decimals", "Decimals")}</th>
              <th>{t("admin.quality.col.sortOrder", "Order")}</th>
              <th>{t("admin.quality.col.active", "Active")}</th>
              <th style={{ textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((p) => (
              <tr key={p.id}>
                <td><code>{p.key}</code></td>
                <td>{localizedName({ name: p.key, nameI18n: p.nameI18n }, i18n.resolvedLanguage ?? "sr")}</td>
                <td>{p.unit}</td>
                <td>{p.decimals}</td>
                <td>{p.sortOrder}</td>
                <td>{p.active ? t("common.yes", "Yes") : t("common.no", "No")}</td>
                <td style={{ textAlign: "right" }}>
                  <button type="button" className="btn sm" disabled={!canWrite} onClick={() => setEditing(p)}>
                    {t("action.edit", "Edit")}
                  </button>
                  {p.active && canFull && (
                    <button type="button" className="btn sm" onClick={() => void retire(p)}>
                      {t("admin.quality.retire", "Retire")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canWrite && (creating || editing) && (
        <ParameterForm
          parameter={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ParameterForm({
  parameter,
  onClose,
  onSaved,
}: {
  parameter: QualityParameter | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [key, setKey] = useState(parameter?.key ?? "");
  const [unit, setUnit] = useState(parameter?.unit ?? "%");
  const [decimals, setDecimals] = useState(String(parameter?.decimals ?? 2));
  const [sortOrder, setSortOrder] = useState(String(parameter?.sortOrder ?? 0));
  const [names, setNames] = useState<Record<string, string>>(parameter?.nameI18n ?? {});
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        nameI18n: names,
        unit,
        decimals: Number(decimals),
        sortOrder: Number(sortOrder),
      };
      if (parameter) {
        await qualityApi.updateParameter(parameter.id, body);
      } else {
        await qualityApi.createParameter({ ...body, key: key.trim().toUpperCase() });
      }
      toast.success(t("admin.quality.saved", "Parameter saved."));
      await onSaved();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section style={card}>
      <h2 style={{ margin: 0, fontSize: 14 }}>
        {parameter
          ? t("admin.quality.editTitle", "Edit parameter")
          : t("admin.quality.new", "New parameter")}
      </h2>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={grid}>
          <label style={label}>
            <span style={fieldLabel}>{t("admin.quality.col.key", "Key")}</span>
            <input
              className="input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={!!parameter}
              required
              pattern="[A-Za-z][A-Za-z0-9_]*"
            />
            {parameter && (
              <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                {t("admin.quality.keyImmutable", "The key never changes — recorded analyses point at it.")}
              </span>
            )}
          </label>
          <label style={label}>
            <span style={fieldLabel}>{t("admin.quality.col.unit", "Unit")}</span>
            <input className="input" value={unit} onChange={(e) => setUnit(e.target.value)} required />
          </label>
          <label style={label}>
            <span style={fieldLabel}>{t("admin.quality.col.decimals", "Decimals")}</span>
            <input
              type="number"
              min="0"
              max="4"
              className="input"
              value={decimals}
              onChange={(e) => setDecimals(e.target.value)}
            />
          </label>
          <label style={label}>
            <span style={fieldLabel}>{t("admin.quality.col.sortOrder", "Order")}</span>
            <input
              type="number"
              className="input"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </label>
        </div>

        <div>
          <span style={fieldLabel}>{t("admin.quality.namesPerLanguage", "Name, per language")}</span>
          <div style={grid}>
            {SUPPORTED_LOCALES.map((loc) => (
              <label key={loc} style={label}>
                <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{loc}</span>
                <input
                  className="input"
                  value={names[loc] ?? ""}
                  onChange={(e) => setNames((prev) => ({ ...prev, [loc]: e.target.value }))}
                />
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="btn primary" disabled={busy}>
            {t("common.save", "Save")}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </button>
        </div>
      </form>
    </section>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};
const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 12,
};
const label: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--ink-3)",
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
